/**
 * Browser launch retry helpers for transient Playwright startup failures.
 *
 * @module playwright-client/launch-retry
 */

import type { BrowserLogger } from '../logger.js';

const TRANSIENT_BROWSER_LAUNCH_PATTERNS = [
  /failed to connect/i,
  /syscall["':\s]+connect/i,
  /\bENOENT\b/i,
  /browser has been closed/i,
  /target page, context or browser has been closed/i,
  /failed to launch/i,
  /\bSIGKILL\b/i,
  /process was terminated/i,
];

const BROWSER_LAUNCH_TIMEOUT_PATTERN = /(?:\bTimeout \d+ms exceeded\b|\btimed out after \d+ms\b)/i;

function abortError(signal: AbortSignal, label: string): Error {
  if (signal.reason instanceof Error) return signal.reason;

  const error = new Error(`${label} aborted`);
  error.name = 'AbortError';
  return error;
}

function throwIfAborted(signal: AbortSignal | undefined, label: string): void {
  if (signal?.aborted) throw abortError(signal, label);
}

async function waitForRetryDelay(
  delayMs: number,
  signal: AbortSignal | undefined,
  label: string
): Promise<void> {
  if (!signal) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return;
  }

  throwIfAborted(signal, label);
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    const onAbort = () => {
      clearTimeout(timeout);
      signal.removeEventListener('abort', onAbort);
      reject(abortError(signal, label));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();
  });
}

function runAbortableAttempt<T>(
  operation: () => Promise<T>,
  options: {
    signal: AbortSignal | undefined;
    label: string;
    logger: BrowserLogger;
    onLateSuccess: ((value: T) => Promise<void> | void) | undefined;
  }
): Promise<T> {
  const { signal } = options;
  throwIfAborted(signal, options.label);
  if (!signal) return operation();

  return new Promise<T>((resolve, reject) => {
    let aborted = false;
    const onAbort = () => {
      aborted = true;
      signal.removeEventListener('abort', onAbort);
      reject(abortError(signal, options.label));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();

    void Promise.resolve()
      .then(() => {
        throwIfAborted(signal, options.label);
        return operation();
      })
      .then(
        (value) => {
          signal.removeEventListener('abort', onAbort);
          if (!aborted) {
            resolve(value);
            return;
          }

          options.logger.warn('[DirectPlaywright] Browser launch completed after cancellation', {
            label: options.label,
          });
          void Promise.resolve()
            .then(() => options.onLateSuccess?.(value))
            .catch((error) => {
              options.logger.warn('[DirectPlaywright] Failed to dispose late browser launch', {
                label: options.label,
                error: formatBrowserLaunchError(error),
              });
            });
        },
        (error) => {
          signal.removeEventListener('abort', onAbort);
          if (!aborted) reject(error);
        }
      );
  });
}

export function formatBrowserLaunchError(error: unknown): string {
  if (error instanceof Error) {
    const extra = error as Error & {
      code?: string;
      errno?: number;
      syscall?: string;
      cause?: unknown;
    };

    const parts = [error.message];
    if (extra.code) parts.push(`code=${extra.code}`);
    if (typeof extra.errno === 'number') parts.push(`errno=${extra.errno}`);
    if (extra.syscall) parts.push(`syscall=${extra.syscall}`);
    if (extra.cause) parts.push(`cause=${formatBrowserLaunchError(extra.cause)}`);
    return parts.join(' ');
  }

  return String(error);
}

export function isTransientBrowserLaunchError(error: unknown): boolean {
  const text = formatBrowserLaunchError(error);
  return TRANSIENT_BROWSER_LAUNCH_PATTERNS.some((pattern) => pattern.test(text));
}

export function isBrowserLaunchTimeoutError(error: unknown): boolean {
  return BROWSER_LAUNCH_TIMEOUT_PATTERN.test(formatBrowserLaunchError(error));
}

export async function withBrowserLaunchRetry<T>(
  operation: (attempt: number) => Promise<T>,
  options: {
    logger: BrowserLogger;
    label: string;
    maxAttempts?: number;
    initialDelayMs?: number;
    signal?: AbortSignal;
    retryTimeouts?: boolean;
    onLateSuccess?: (value: T) => Promise<void> | void;
  }
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? 3;
  const initialDelayMs = options.initialDelayMs ?? 150;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    throwIfAborted(options.signal, options.label);
    try {
      return await runAbortableAttempt(() => operation(attempt), {
        signal: options.signal,
        label: options.label,
        logger: options.logger,
        onLateSuccess: options.onLateSuccess,
      });
    } catch (error) {
      lastError = error;

      throwIfAborted(options.signal, options.label);

      const retryableTimeout =
        options.retryTimeouts !== false || !isBrowserLaunchTimeoutError(error);
      if (attempt >= maxAttempts || !isTransientBrowserLaunchError(error) || !retryableTimeout) {
        throw error;
      }

      const delayMs = initialDelayMs * 2 ** (attempt - 1);
      options.logger.warn('[DirectPlaywright] Browser launch failed transiently, retrying', {
        label: options.label,
        attempt,
        nextAttempt: attempt + 1,
        delayMs,
        error: formatBrowserLaunchError(error),
      });
      await waitForRetryDelay(delayMs, options.signal, options.label);
    }
  }

  throw lastError;
}
